/**
 * Particle Effects System for Super Reversi
 *
 * Creates visual effects based on move quality grades
 */

import { PARTICLE_PRESETS, getPresetForGrade, type ParticlePresetConfig } from './effects/particle-presets';
import { GreenLotusEffect, type Position } from './effects/green-lotus-effect';
import { UnlimitedBladeWorksEffect } from './effects/unlimited-blade-works';
import { DivineSwordThunderEffect } from './effects/divine-sword-thunder';
import { NineFinaleEffect } from './effects/nine-finale-effect';
import { IceSpearEffect } from './effects/ice-spear-effect';

export type Grade = 'C' | 'B' | 'A' | 'S' | 'SS' | 'SSS';

export interface SpecialEffect {
  corner?: boolean;
  edge?: boolean;
  massive?: boolean;  // 10+ flips
  chain?: boolean;    // Triggered by previous move
  firstMove?: boolean;
  comeback?: boolean; // When losing player makes good move
  domination?: boolean; // When winning player extends lead
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  alpha: number;
  type: 'circle' | 'star' | 'spark' | 'text';
  text?: string; // 用於文字粒子
  fontSize?: number; // 文字大小
}

export interface FlySwordConfig {
  color: string;
  trailColor: string;
  size: number;
  speed: number;
  glowIntensity: number;
}

export class FlySword {
  public pos: { x: number; y: number };
  public vel: { x: number; y: number };
  public target: { x: number; y: number } | null;
  public tail: Array<{ x: number; y: number }>;
  public alive: boolean;
  public life: number;
  public maxLife: number;
  private tailLength: number;
  private config: FlySwordConfig;

  constructor(x: number, y: number, target: { x: number; y: number } | null, config: FlySwordConfig) {
    this.pos = { x, y };
    this.vel = { x: 0, y: -6 };
    this.target = target;
    this.tail = [];
    this.tailLength = 20; // Shorter trail for cleaner effect
    this.alive = true;
    this.life = 2.0;      // Reduced visibility duration
    this.maxLife = 2.0;
    this.config = config;

    console.log(`🗡️ FlySword created at (${x}, ${y}) targeting (${target?.x}, ${target?.y})`);
  }

  update(): void {
    if (!this.alive) {return;}

    // Update life
    this.life -= 0.016; // ~60fps
    if (this.life <= 0) {
      this.alive = false;
      return;
    }

    // Simple guidance towards target
    if (this.target) {
      const dx = this.target.x - this.pos.x;
      const dy = this.target.y - this.pos.y;
      const distance = Math.hypot(dx, dy);

      if (distance > 5) {
        // Gradually steer towards target
        this.vel.x += (dx / distance) * 0.4;
        this.vel.y += (dy / distance) * 0.4;

        // Limit speed
        const speed = Math.hypot(this.vel.x, this.vel.y);
        if (speed > this.config.speed) {
          this.vel.x *= this.config.speed / speed;
          this.vel.y *= this.config.speed / speed;
        }
      } else {
        // Close to target, mark for death
        this.life = Math.min(this.life, 0.3);
      }
    }

    // Update position
    this.pos.x += this.vel.x;
    this.pos.y += this.vel.y;

    // Update tail
    this.tail.push({ x: this.pos.x, y: this.pos.y });
    if (this.tail.length > this.tailLength) {
      this.tail.shift();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (!this.alive || this.tail.length < 2) {return;}

    const alpha = this.life / this.maxLife;

    // Draw glowing trail
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (let i = 1; i < this.tail.length; i++) {
      const prev = this.tail[i - 1];
      const curr = this.tail[i];
      if (!prev || !curr) {continue;}

      const trailAlpha = (i / this.tail.length) * alpha * 0.6;
      ctx.strokeStyle = this.config.trailColor.replace('1)', `${trailAlpha})`);
      ctx.lineWidth = 2 + i / 3;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(curr.x, curr.y);
      ctx.stroke();
    }

    ctx.restore();

    // Draw sword body
    const angle = Math.atan2(this.vel.y, this.vel.x);
    ctx.save();
    ctx.translate(this.pos.x, this.pos.y);
    ctx.rotate(angle);

    // Apply glow effect
    ctx.shadowColor = this.config.color;
    ctx.shadowBlur = this.config.glowIntensity * alpha;
    ctx.globalAlpha = alpha;

    // Sword body (rectangle)
    ctx.fillStyle = this.config.color;
    ctx.fillRect(-this.config.size, -2, this.config.size, 4);

    // Sword tip (triangle)
    ctx.fillStyle = this.config.color;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(this.config.size * 0.3, -3);
    ctx.lineTo(this.config.size * 0.3, 3);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }
}

export class ParticleSystem {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private swords: FlySword[] = [];
  private greenLotusEffects: GreenLotusEffect[] = [];
  private ubwEffects: UnlimitedBladeWorksEffect[] = [];
  private divineThunderEffects: DivineSwordThunderEffect[] = [];
  private nineFinaleEffects: NineFinaleEffect[] = [];
  private iceSpearEffects: IceSpearEffect[] = [];
  private animationId: number | null = null;
  private lastTime = 0;
  private enabled = true;

  constructor() {
    // Create canvas for particles
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'particle-canvas';
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '100';

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {throw new Error('Could not get canvas context');}
    this.ctx = ctx;
  }

  initialize(container: HTMLElement): void {
    // Set canvas size
    const rect = container.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;

    // Add to container
    container.style.position = 'relative';
    container.appendChild(this.canvas);

    // Start animation loop only if enabled
    if (this.enabled) {
      this.startAnimation();
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      if (!this.animationId) {
        this.startAnimation();
      }
    } else {
      this.stopAnimation();
      this.clearParticles();
    }
  }

  private stopAnimation(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private clearParticles(): void {
    this.particles = [];
    this.swords = [];
    this.greenLotusEffects = [];
    this.ubwEffects = [];
    this.divineThunderEffects = [];
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private startAnimation(): void {
    const animate = (time: number) => {
      const deltaTime = time - this.lastTime;
      this.lastTime = time;

      this.update(deltaTime / 1000); // Convert to seconds
      this.render();

      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  private update(deltaTime: number): void {
    // Update all particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) {continue;}

      // Update position
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      // Update life
      p.life -= deltaTime;

      // Update alpha based on life
      p.alpha = Math.max(0, p.life / p.maxLife);

      // Apply gravity for some effects
      if (p.type === 'spark') {
        p.vy += 200 * deltaTime; // Gravity
      }

      // Remove dead particles
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }

    // Update all swords
    for (let i = this.swords.length - 1; i >= 0; i--) {
      const sword = this.swords[i];
      if (!sword) {continue;}

      sword.update();

      // Remove dead swords
      if (!sword.alive) {
        this.swords.splice(i, 1);
      }
    }

    // Update all green lotus effects
    for (let i = this.greenLotusEffects.length - 1; i >= 0; i--) {
      const effect = this.greenLotusEffects[i];
      if (!effect) {continue;}

      effect.update(deltaTime);

      // Remove dead effects
      if (effect.dead) {
        this.greenLotusEffects.splice(i, 1);
      }
    }

    // Update all UBW effects
    for (let i = this.ubwEffects.length - 1; i >= 0; i--) {
      const effect = this.ubwEffects[i];
      if (!effect) {continue;}

      effect.update(deltaTime);

      // Remove dead effects
      if (effect.dead) {
        this.ubwEffects.splice(i, 1);
      }
    }

    // Update all Divine Thunder effects
    for (let i = this.divineThunderEffects.length - 1; i >= 0; i--) {
      const effect = this.divineThunderEffects[i];
      if (!effect) {continue;}

      effect.update(deltaTime);

      // Remove dead effects
      if (effect.dead) {
        this.divineThunderEffects.splice(i, 1);
      }
    }

    // Update all Nine Finale effects
    for (let i = this.nineFinaleEffects.length - 1; i >= 0; i--) {
      const effect = this.nineFinaleEffects[i];
      if (!effect) {continue;}

      effect.update(deltaTime);

      // Remove finished effects
      if (effect.isFinished()) {
        this.nineFinaleEffects.splice(i, 1);
      }
    }

    // Update all Ice Spear effects
    for (let i = this.iceSpearEffects.length - 1; i >= 0; i--) {
      const effect = this.iceSpearEffects[i];
      if (!effect) {continue;}

      effect.update(deltaTime);

      // Remove dead effects
      if (effect.dead) {
        this.iceSpearEffects.splice(i, 1);
      }
    }
  }

  private render(): void {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw all particles
    for (const p of this.particles) {
      this.ctx.save();
      this.ctx.globalAlpha = p.alpha;

      if (p.type === 'circle') {
        this.drawCircle(p);
      } else if (p.type === 'star') {
        this.drawStar(p);
      } else if (p.type === 'spark') {
        this.drawSpark(p);
      } else if (p.type === 'text') {
        this.drawText(p);
      }

      this.ctx.restore();
    }

    // Draw all swords
    for (const sword of this.swords) {
      sword.draw(this.ctx);
    }

    // Draw all green lotus effects
    for (const effect of this.greenLotusEffects) {
      effect.draw(this.ctx);
    }

    // Draw all UBW effects
    for (const effect of this.ubwEffects) {
      effect.draw(this.ctx);
    }

    // Draw all Divine Thunder effects
    for (const effect of this.divineThunderEffects) {
      effect.draw(this.ctx);
    }

    // Draw all Nine Finale effects
    for (const effect of this.nineFinaleEffects) {
      effect.render(this.ctx);
    }

    // Draw all Ice Spear effects
    for (const effect of this.iceSpearEffects) {
      effect.draw(this.ctx);
    }
  }

  private drawCircle(p: Particle): void {
    this.ctx.beginPath();
    this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    this.ctx.fillStyle = p.color;
    this.ctx.fill();

    // Add glow effect
    this.ctx.shadowBlur = 10;
    this.ctx.shadowColor = p.color;
    this.ctx.fill();
  }

  private drawStar(p: Particle): void {
    const spikes = 5;
    const outerRadius = p.size;
    const innerRadius = p.size * 0.5;

    this.ctx.beginPath();
    this.ctx.translate(p.x, p.y);

    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / spikes;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      if (i === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }
    }

    this.ctx.closePath();
    this.ctx.fillStyle = p.color;
    this.ctx.fill();

    this.ctx.translate(-p.x, -p.y);
  }

  private drawSpark(p: Particle): void {
    this.ctx.beginPath();
    this.ctx.moveTo(p.x, p.y);
    this.ctx.lineTo(p.x - p.vx * 0.1, p.y - p.vy * 0.1);
    this.ctx.strokeStyle = p.color;
    this.ctx.lineWidth = p.size;
    this.ctx.lineCap = 'round';
    this.ctx.stroke();
  }

  private drawText(p: Particle): void {
    if (!p.text || !p.fontSize) {return;}

    // 設定文字樣式
    this.ctx.font = `bold ${p.fontSize}px Arial, sans-serif`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillStyle = p.color;

    // 新增發光效果
    this.ctx.shadowBlur = 15;
    this.ctx.shadowColor = p.color;

    // 新增描邊效果讓文字更突出
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    this.ctx.lineWidth = 3;
    this.ctx.strokeText(p.text, p.x, p.y);

    // 填充文字
    this.ctx.fillText(p.text, p.x, p.y);
  }

  // Enhanced move grade calculation with special effects
  calculateGrade(flippedCount: number, position: number, specialEffects?: SpecialEffect): Grade {
    // Corner positions are special
    const isCorner = position === 0 || position === 7 || position === 56 || position === 63;

    // Edge positions (not corners)
    const row = Math.floor(position / 8);
    const col = position % 8;
    const isEdge = row === 0 || row === 7 || col === 0 || col === 7;

    let score = flippedCount * 10;

    // Bonus for strategic positions
    if (isCorner) {score += 60;}
    else if (isEdge) {score += 20;}

    // Special bonuses
    if (specialEffects?.massive) {score += 40;}      // 10+ flips
    if (specialEffects?.chain) {score += 30;}       // Chain reaction
    if (specialEffects?.comeback) {score += 25;}    // Comeback move
    if (specialEffects?.domination) {score += 20;}  // Domination move
    if (specialEffects?.firstMove) {score -= 10;}   // First moves are usually basic

    // Adjusted grading system for balanced effects
    if (score >= 100 || flippedCount >= 15) {return 'SSS';}  // LEGENDARY (rare)
    if (score >= 70 || flippedCount >= 10) {return 'SS';}   // MYTHICAL
    if (score >= 50 || flippedCount >= 7) {return 'S';}     // EPIC
    if (score >= 30 || flippedCount >= 5) {return 'A';}     // GREAT
    if (score >= 15 || flippedCount >= 3) {return 'B';}     // GOOD
    return 'C';                                            // BASIC
  }

  // Enhanced trigger system with more variations - now supports directional particles
  triggerMoveEffect(x: number, y: number, grade: Grade, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    if (!this.enabled) {return;}

    // 首先創建飄起的等級文字
    this.createFloatingGradeText(x, y, grade);

    // 🔥⚔️ SWORD MODE: SSS uses ONLY sword effects for maximum impact! ⚔️🔥
    if (grade === 'SSS') {
      console.log(`🗡️ SWORD MODE ACTIVATED: ${grade} grade with ${flippedCount} flips!`);

      // Use sword effects for SSS grade
      console.log('⚔️ Triggering DIVINE GOLDEN SWORDS! ⚔️');
      setTimeout(() => this.createSSSEffect(x, y, flippedCount, specialEffects, flippedPositions), 0);

      return; // Skip preset particles for sword modes
    }

    // Use preset system for other grades (C, B, S)
    this.createPresetEffect(x, y, grade, flippedCount, specialEffects, flippedPositions);

    // Additional effects for S grade only (A now uses swords)
    if (grade === 'S' && flippedCount >= 7) {
      setTimeout(() => this.createSEffect(x, y, flippedCount, specialEffects, flippedPositions), 100);
    }
  }

  // 使用預設配置創建特效 - 支持方向性粒子
  private createPresetEffect(x: number, y: number, grade: Grade, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    const presetType = getPresetForGrade(grade);
    const preset = PARTICLE_PRESETS[presetType];

    if (!preset) {
      console.warn(`No preset found for grade ${grade}`);
      return;
    }

    console.log(`🎨 ${grade} 級移動特效: ${presetType} (${flippedCount} 翻轉)`);

    // Special handling for B-tier Green Lotus effect
    if (grade === 'B' && presetType === 'green_lotus') {
      this.createGreenLotusEffect(x, y, flippedPositions || []);
      return;
    }

    // Special handling for C-tier UBW effect
    if (grade === 'C' && presetType === 'unlimited_blade_works') {
      this.createUnlimitedBladeWorksEffect(x, y, flippedPositions || []);
      return;
    }

    // Special handling for S-tier Divine Thunder effect
    if (grade === 'S' && presetType === 'divine_thunder') {
      this.createDivineThunderEffect(x, y, flippedPositions || []);
      return;
    }

    // Special handling for SS-tier Nine Finale effect
    if (grade === 'SS' && presetType === 'nine_finale') {
      this.createNineFinaleEffect(x, y, flippedPositions || []);
      return;
    }

    // Special handling for A-tier Ice Spear effect
    if (grade === 'A') {
      this.createIceSpearEffect(x, y, flippedPositions || []);
      return;
    }

    // 計算翻轉方向向量
    const flipDirections = this.calculateFlipDirections(x, y, flippedPositions);

    // 根據翻轉數量調整粒子數量
    const intensityMultiplier = Math.min(1 + (flippedCount - 1) * 0.1, 2.0);
    const particleCount = Math.floor(
      (preset.spawning.count.min + Math.random() * (preset.spawning.count.max - preset.spawning.count.min)) * intensityMultiplier,
    );

    // 創建預設效果的粒子
    for (let i = 0; i < particleCount; i++) {
      setTimeout(() => {
        this.createPresetParticle(x, y, preset, i, particleCount, flipDirections);
      }, Math.random() * (preset.spawning.delay.max - preset.spawning.delay.min) + preset.spawning.delay.min);
    }

    // 特殊效果增強
    if (specialEffects?.corner && grade !== 'C') {
      this.addCornerEnhancement(x, y, preset, flipDirections);
    }
    if (specialEffects?.massive && flippedCount >= 8) {
      this.addMassiveEnhancement(x, y, preset, flipDirections);
    }
  }

  // 計算翻轉方向向量
  private calculateFlipDirections(sourceX: number, sourceY: number, flippedPositions?: Array<{x: number, y: number}>): Array<{angle: number, distance: number}> {
    if (!flippedPositions || flippedPositions.length === 0) {
      // 如果沒有翻轉位置，返回8個均勻分布的方向
      return Array.from({length: 8}, (_, i) => ({
        angle: (i / 8) * Math.PI * 2,
        distance: 100,
      }));
    }

    // 計算從源點到每個翻轉位置的方向和距離
    return flippedPositions.map(pos => {
      const dx = pos.x - sourceX;
      const dy = pos.y - sourceY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      return {
        angle,
        distance: Math.max(distance, 50), // 最小距離50px
      };
    });
  }

  // 創建飛劍 - 用於 SS 和 SSS 級別特效
  private createSwords(sourceX: number, sourceY: number, targets: Array<{x: number, y: number}>, config: FlySwordConfig, count: number, delay: number = 0): void {
    // Limit maximum swords for performance - dramatically increased for sword prominence
    const maxSwords = 60; // Dramatically increased from 25
    if (this.swords.length >= maxSwords) {
      console.warn(`⚠️ Sword limit reached (${maxSwords}), skipping creation`);
      return;
    }

    const actualCount = Math.min(count, maxSwords - this.swords.length);
    console.log(`⚔️ createSwords: Creating ${actualCount} swords from (${sourceX}, ${sourceY}) with ${delay}ms delay`);

    for (let i = 0; i < actualCount; i++) {
      setTimeout(() => {
        // Choose target (either specific target or random position)
        let target: { x: number; y: number } | null = null;

        if (targets.length > 0) {
          // Cycle through targets
          target = targets[i % targets.length] || targets[0] || null;
        } else {
          // Random target around the source
          const angle = (i / actualCount) * Math.PI * 2;
          const distance = 200 + Math.random() * 300;
          target = {
            x: sourceX + Math.cos(angle) * distance,
            y: sourceY + Math.sin(angle) * distance,
          };
        }

        // Add some random offset to source position for sword spawn
        const spawnRadius = 30;
        const spawnAngle = Math.random() * Math.PI * 2;
        const spawnX = sourceX + Math.cos(spawnAngle) * spawnRadius;
        const spawnY = sourceY + Math.sin(spawnAngle) * spawnRadius;

        const sword = new FlySword(spawnX, spawnY, target, config);
        this.swords.push(sword);
      }, delay + i * 30); // Faster stagger for immediate effect
    }
  }

  // 創建預設粒子 - 支持方向性發射
  private createPresetParticle(x: number, y: number, preset: ParticlePresetConfig, index: number, total: number, flipDirections?: Array<{angle: number, distance: number}>): void {
    const colors = preset.colors;
    const color = colors[Math.floor(Math.random() * colors.length)] || colors[0] || '#ffffff';

    // 計算角度和速度 - 支持方向性發射
    let angle: number;
    let speed: number;

    // 如果有翻轉方向，使用方向性發射
    if (flipDirections && flipDirections.length > 0) {
      // 根據粒子索引選擇翻轉方向
      const directionIndex = Math.floor((index / total) * flipDirections.length);
      const direction = flipDirections[directionIndex] || flipDirections[0];

      if (direction) {
        // 在選定方向的基礎上添加一些隨機偏移
        const baseAngle = direction.angle;
        const spreadAngle = preset.spawning.spread.angle || (Math.PI / 6); // 30度默認擴散角
        angle = baseAngle + (Math.random() - 0.5) * spreadAngle;
      } else {
        // Fallback if no direction found
        angle = Math.random() * Math.PI * 2;
      }

      speed = preset.physics.velocity.min + Math.random() * (preset.physics.velocity.max - preset.physics.velocity.min);
    } else {
      // 原有的行為模式
      switch (preset.behavior.movement) {
        case 'explode':
          angle = (index / total) * Math.PI * 2;
          speed = preset.physics.velocity.min + Math.random() * (preset.physics.velocity.max - preset.physics.velocity.min);
          break;
        case 'float':
          angle = Math.random() * Math.PI * 2;
          speed = preset.physics.velocity.min + Math.random() * (preset.physics.velocity.max - preset.physics.velocity.min);
          break;
        case 'rise':
          angle = Math.PI * 1.5 + (Math.random() - 0.5) * preset.spawning.spread.angle;
          speed = preset.physics.velocity.min + Math.random() * (preset.physics.velocity.max - preset.physics.velocity.min);
          break;
        case 'chain':
          angle = Math.random() * Math.PI * 2;
          speed = preset.physics.velocity.max;
          break;
        case 'implode':
          angle = Math.random() * Math.PI * 2;
          speed = preset.physics.velocity.min + Math.random() * (preset.physics.velocity.max - preset.physics.velocity.min);
          break;
        default:
          angle = Math.random() * Math.PI * 2;
          speed = 100;
      }
    }

    // 計算位置偏移
    const spreadRadius = preset.spawning.spread.radius;
    const spreadAngle = Math.random() * preset.spawning.spread.angle - preset.spawning.spread.angle / 2;
    const startX = x + Math.cos(spreadAngle) * Math.random() * spreadRadius;
    const startY = y + Math.sin(spreadAngle) * Math.random() * spreadRadius;

    // 創建粒子
    const size = preset.visual.size.min + Math.random() * (preset.visual.size.max - preset.visual.size.min);
    const life = (preset.animation.lifespan.min + Math.random() * (preset.animation.lifespan.max - preset.animation.lifespan.min)) / 1000;

    this.particles.push({
      x: startX,
      y: startY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: life,
      maxLife: life,
      size: size,
      color: color,
      alpha: preset.visual.opacity.start,
      type: preset.type === 'snowflake' ? 'circle' : preset.type === 'lightning' ? 'spark' : 'circle',
    });
  }

  // 角落增強效果 - 支持方向性
  private addCornerEnhancement(x: number, y: number, preset: ParticlePresetConfig, flipDirections?: Array<{angle: number, distance: number}>): void {
    // 添加金色光環
    for (let i = 0; i < 12; i++) {
      let angle: number;

      if (flipDirections && flipDirections.length > 0) {
        // 使用翻轉方向，添加偏移
        const directionIndex = Math.floor((i / 12) * flipDirections.length);
        const direction = flipDirections[directionIndex] || flipDirections[0];
        if (direction) {
          angle = direction.angle + (Math.random() - 0.5) * (Math.PI / 4);
        } else {
          angle = (i / 12) * Math.PI * 2;
        }
      } else {
        angle = (i / 12) * Math.PI * 2;
      }

      this.particles.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * 80,
        vy: Math.sin(angle) * 80,
        life: 1.0,
        maxLife: 1.0,
        size: 3,
        color: '#ffd700',
        alpha: 0.8,
        type: 'circle',
      });
    }
  }

  // 大規模增強效果 - 支持方向性
  private addMassiveEnhancement(x: number, y: number, preset: ParticlePresetConfig, flipDirections?: Array<{angle: number, distance: number}>): void {
    // 添加衝擊波
    for (let wave = 0; wave < 3; wave++) {
      setTimeout(() => {
        for (let i = 0; i < 20; i++) {
          let angle: number;

          if (flipDirections && flipDirections.length > 0) {
            // 使用翻轉方向，添加偏移
            const directionIndex = Math.floor((i / 20) * flipDirections.length);
            const direction = flipDirections[directionIndex] || flipDirections[0];
            if (direction) {
              angle = direction.angle + (Math.random() - 0.5) * (Math.PI / 3);
            } else {
              angle = (i / 20) * Math.PI * 2;
            }
          } else {
            angle = (i / 20) * Math.PI * 2;
          }

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * (150 + wave * 50),
            vy: Math.sin(angle) * (150 + wave * 50),
            life: 0.8,
            maxLife: 0.8,
            size: 2,
            color: '#ffffff',
            alpha: 0.6 - wave * 0.2,
            type: 'circle',
          });
        }
      }, wave * 100);
    }
  }

  private createSSSEffect(x: number, y: number, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    // 🌟 LEGENDARY SSS-GRADE - GOLDEN MAGNIFICENCE! 🌟
    const goldColors = [
      '#ffd700', '#ffdf00', '#ffed4e', '#fff700', '#ffa500', '#daa520',
      '#b8860b', '#ffeaa7', '#fdcb6e', '#f39c12', '#e67e22', '#d4af37',
    ];

    console.log('🌟✨🎆 LEGENDARY SSS-GRADE MOVE - REALITY SHATTER! 🎆✨🌟');

    // ⚔️ DIVINE GOLDEN SWORDS - Enhanced heavenly judgment! ⚔️
    const goldenSwordConfig: FlySwordConfig = {
      color: '#ffed4a',    // Brighter golden yellow for maximum contrast
      trailColor: 'rgba(255, 237, 74, 1)',
      size: 90,            // MASSIVE size for supreme visibility
      speed: 5,            // Slowest for maximum drama
      glowIntensity: 100,   // Maximum possible glow for absolute dominance
    };

    // Create targets from flipped positions
    const swordTargets = flippedPositions || [];
    const swordCount = Math.min(35, Math.max(25, Math.floor(flippedCount * 2.5))); // 25-35 swords - MASSIVE ARMY!

    console.log(`⚔️ Creating ${swordCount} DIVINE GOLDEN SWORDS targeting ${swordTargets.length} positions!`);

    // Create divine golden swords in multiple waves for maximum impact
    this.createSwords(x, y, swordTargets, goldenSwordConfig, Math.floor(swordCount * 0.6), 0);
    setTimeout(() => {
      console.log('⚔️ Second wave of GOLDEN SWORDS incoming!');
      this.createSwords(x, y, swordTargets, goldenSwordConfig, Math.floor(swordCount * 0.4), 0);
    }, 100);

    // ⚔️ MINIMAL GOLDEN SPARKLES - Just accent the divine swords! ⚔️
    console.log('⚔️ Adding minimal golden sparkles to accent the divine swords');

    // ⚔️ COMPLETELY OBLITERATED - GOLDEN SWORDS RULE ALL! ⚔️
    console.log('⚔️ ALL GOLDEN SPARKLES BANISHED - ONLY DIVINE SWORDS REMAIN!');
    // No sparkles, no stars - the golden swords are the only light needed!

    // Special effect variations - minimal, sword-focused
    if (specialEffects?.corner) {
      console.log('⚔️ LEGENDARY CORNER SWORD MASTERY!');
      // Add 3 more swords for corner effect instead of heavy particles
      setTimeout(() => {
        this.createSwords(x, y, swordTargets, goldenSwordConfig, 3, 0);
      }, 500);
    }
    if (specialEffects?.comeback) {
      console.log('⚔️ LEGENDARY COMEBACK SWORD STORM!');
      // Add extra sword wave for comeback effect
      setTimeout(() => {
        this.createSwords(x, y, swordTargets, goldenSwordConfig, 2, 0);
      }, 600);
    }
  }

  private createSSEffect(x: number, y: number, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    // 🌪️ MYTHICAL SS-GRADE - RED STORM! 🌪️
    const redColors = ['#ff0000', '#dc143c', '#b22222', '#cd5c5c', '#ff6347', '#ff4500', '#ff1493', '#8b0000', '#a52a2a', '#c41e3a'];

    console.log('🌪️💎 MYTHICAL SS-GRADE MOVE! 💎🌪️');

    // 🔥 FLAME SWORDS - Enhanced for maximum visibility! 🔥
    const flameSwordConfig: FlySwordConfig = {
      color: '#ff1100',    // Brighter red for better contrast
      trailColor: 'rgba(255, 17, 0, 1)',
      size: 70,            // Dramatically increased from 40
      speed: 6,            // Even slower for dramatic effect
      glowIntensity: 90,    // Maximum glow for dominance
    };

    // Create targets from flipped positions
    const swordTargets = flippedPositions || [];
    const swordCount = Math.min(20, Math.max(15, Math.floor(flippedCount * 1.5))); // 15-20 swords - tripled!

    console.log(`🔥 Creating ${swordCount} FLAME SWORDS targeting ${swordTargets.length} positions!`);

    // Create flame swords immediately for responsive effect
    this.createSwords(x, y, swordTargets, flameSwordConfig, swordCount, 0);

    // 🔥 MINIMAL FIRE SPARKS - Just accent the swords, don't overwhelm them! 🔥
    console.log('🔥 Adding minimal fire sparks to accent the flame swords');

    // 🔥 COMPLETELY REMOVED - FLAME SWORDS REIGN SUPREME! 🔥
    console.log('🔥 ALL FIRE PARTICLES ELIMINATED - PURE SWORD DOMINATION!');
    // No sparks, no flames - only the mighty flame swords!

    // Special effects - keep minimal for sword focus
    if (specialEffects?.massive) {
      console.log('🔥 MASSIVE FLAME SWORD STORM!');
      // Add 2 more swords for massive effect instead of heavy particles
      setTimeout(() => {
        this.createSwords(x, y, swordTargets, flameSwordConfig, 2, 0);
      }, 400);
    }
  }

  private createSEffect(x: number, y: number, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    // ULTRA SPECTACULAR S-grade effect - PURPLE MAJESTY!
    const purpleColors = ['#dda0dd', '#e6b8ea', '#d8bfd8', '#9932cc', '#ba55d3', '#9370db', '#8a2be2', '#9966cc', '#c8a2c8'];

    console.log('🎆 LEGENDARY S-GRADE MOVE! 🎆');

    // MASSIVE RING BURST - 5 waves with directional preference!
    const flipDirections = this.calculateFlipDirections(x, y, flippedPositions);

    for (let wave = 0; wave < 5; wave++) {
      setTimeout(() => {
        for (let j = 0; j < 40; j++) {
          let angle: number;

          if (flipDirections.length > 0 && Math.random() < 0.65) {
            // 65% 機率朝向翻轉方向爆發
            const directionIndex = Math.floor((j / 40) * flipDirections.length);
            const direction = flipDirections[directionIndex] || flipDirections[0];
            if (direction) {
              angle = direction.angle + (Math.random() - 0.5) * (Math.PI / 2.5); // 72度擴散
            } else {
              angle = (j / 40) * Math.PI * 2;
            }
          } else {
            // 35% 機率保持環形爆發
            angle = (j / 40) * Math.PI * 2;
          }

          const speed = 300 + wave * 80;
          const wobble = Math.sin(j * 0.5) * 20; // Add wobble

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed + wobble,
            vy: Math.sin(angle) * speed + wobble,
            life: 1.5,
            maxLife: 1.5,
            size: Math.random() * 5 + 3,
            color: purpleColors[wave % purpleColors.length] ?? '#9932cc',
            alpha: 1,
            type: 'circle',
          });
        }
      }, wave * 80);
    }

    // GIANT SPIRAL VORTEX
    for (let i = 0; i < 80; i++) {
      const angle = (i / 8) * Math.PI;
      const radius = i * 4;

      setTimeout(() => {
        this.particles.push({
          x: x + Math.cos(angle) * radius * 0.3,
          y: y + Math.sin(angle) * radius * 0.3,
          vx: Math.cos(angle + Math.PI/2) * 150,
          vy: Math.sin(angle + Math.PI/2) * 150,
          life: 2.0,
          maxLife: 2.0,
          size: Math.random() * 4 + 2,
          color: purpleColors[i % purpleColors.length] ?? '#9932cc',
          alpha: 1,
          type: 'circle',
        });
      }, i * 15);
    }

    // NUCLEAR FOUNTAIN - Always spectacular regardless of flip count
    for (let i = 0; i < 100 + flippedCount * 15; i++) {
      setTimeout(() => {
        this.particles.push({
          x: x + (Math.random() - 0.5) * 40,
          y: y + Math.random() * 20,
          vx: (Math.random() - 0.5) * 400,
          vy: -Math.random() * 500 - 200,
          life: 2.5,
          maxLife: 2.5,
          size: Math.random() * 4 + 2,
          color: purpleColors[Math.floor(Math.random() * purpleColors.length)] ?? '#8a2be2',
          alpha: 1,
          type: Math.random() > 0.3 ? 'spark' : 'circle',
        });
      }, Math.random() * 300);
    }

    // SHOCKWAVE EFFECT - Expanding circles
    for (let wave = 0; wave < 3; wave++) {
      setTimeout(() => {
        for (let angle = 0; angle < Math.PI * 2; angle += 0.1) {
          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * (400 + wave * 100),
            vy: Math.sin(angle) * (400 + wave * 100),
            life: 0.8,
            maxLife: 0.8,
            size: 1,
            color: '#ffffff',
            alpha: 0.8,
            type: 'circle',
          });
        }
      }, wave * 200);
    }

    // STARBURST - Directional explosion
    for (let i = 0; i < 50; i++) {
      let angle: number;

      if (flipDirections.length > 0 && Math.random() < 0.7) {
        // 70% 機率朝向翻轉方向爆炸
        const directionIndex = Math.floor((i / 50) * flipDirections.length);
        const direction = flipDirections[directionIndex] || flipDirections[0];
        if (direction) {
          angle = direction.angle + (Math.random() - 0.5) * (Math.PI / 3); // 60度擴散
        } else {
          angle = (i / 50) * Math.PI * 2;
        }
      } else {
        // 30% 機率保持星形爆炸
        angle = (i / 50) * Math.PI * 2;
      }

      setTimeout(() => {
        this.particles.push({
          x: x,
          y: y,
          vx: Math.cos(angle) * 500,
          vy: Math.sin(angle) * 500,
          life: 1.2,
          maxLife: 1.2,
          size: Math.random() * 6 + 2,
          color: '#9932cc',
          alpha: 1,
          type: 'circle',
        });
      }, i * 20);
    }
  }

  private createAEffectWithSwords(x: number, y: number, flippedCount: number, specialEffects?: SpecialEffect, flippedPositions?: Array<{x: number, y: number}>): void {
    // ❄️ FROST BLUE SWORDS - A-grade elegance! ❄️
    console.log('❄️ A-GRADE FROST SWORDS ACTIVATED! ❄️');

    // ❄️ FROST BLUE SWORDS - Elegant ice blades! ❄️
    const frostSwordConfig: FlySwordConfig = {
      color: '#00bfff',
      trailColor: 'rgba(0, 191, 255, 1)',
      size: 45,        // Dramatically increased from 25
      speed: 8,        // Slower for better visibility
      glowIntensity: 80, // Dramatically enhanced glow
    };

    // Create targets from flipped positions
    const swordTargets = flippedPositions || [];
    const swordCount = Math.min(12, Math.max(8, Math.floor(flippedCount * 1.2))); // 8-12 swords - doubled!

    console.log(`❄️ Creating ${swordCount} FROST BLUE SWORDS targeting ${swordTargets.length} positions!`);

    // Create frost swords immediately for responsive effect
    this.createSwords(x, y, swordTargets, frostSwordConfig, swordCount, 0);

    // COMPLETELY REMOVED - Let swords dominate!
    console.log('❄️ ALL PARTICLES REMOVED - PURE SWORD FOCUS!');
    // No particles - only swords shall rule the battlefield!

    // Special effects
    if (specialEffects?.massive) {
      console.log('❄️ MASSIVE FROST STORM!');
    }
    if (specialEffects?.corner) {
      console.log('❄️ CORNER FROST MASTERY!');
    }
  }

  private createAEffect(x: number, y: number, flippedCount: number, flippedPositions?: Array<{x: number, y: number}>): void {
    // EPIC A-grade effect - AZURE BRILLIANCE!
    const blueColors = ['#87ceeb', '#87cefa', '#00bfff', '#1e90ff', '#6495ed', '#4682b4', '#add8e6', '#b0e0e6', '#afeeee'];

    console.log('⭐ AMAZING A-GRADE MOVE! ⭐');

    // EPIC CROSS SWEEP - Directional waves
    const flipDirections = this.calculateFlipDirections(x, y, flippedPositions);

    if (flipDirections.length > 0) {
      // 使用翻轉方向進行定向掃射
      for (const flipDir of flipDirections) {
        for (let wave = 0; wave < 3; wave++) {
          for (let i = 0; i < 20; i++) {
            setTimeout(() => {
              // 主方向加上一些擴散
              const spread = (Math.random() - 0.5) * (Math.PI / 4); // 45度擴散
              const finalAngle = flipDir.angle + spread;
              const speed = 250 + wave * 50;

              this.particles.push({
                x: x,
                y: y,
                vx: Math.cos(finalAngle) * speed,
                vy: Math.sin(finalAngle) * speed,
                life: 1.0 + wave * 0.2,
                maxLife: 1.0 + wave * 0.2,
                size: Math.random() * 4 + 2,
                color: blueColors[wave % blueColors.length] ?? '#87ceeb',
                alpha: 1,
                type: 'circle',
              });
            }, i * 20 + wave * 150);
          }
        }
      }
    } else {
      // 後備方案：使用原有的8方向掃射
      const directions = [
        [1,0], [0,1], [-1,0], [0,-1],   // Cardinals
        [1,1], [-1,1], [1,-1], [-1,-1],  // Diagonals
      ];

      for (const [dx, dy] of directions as [number, number][]) {
        for (let wave = 0; wave < 3; wave++) {
          for (let i = 0; i < 15; i++) {
            setTimeout(() => {
              this.particles.push({
                x: x,
                y: y,
                vx: dx * (250 + wave * 50),
                vy: dy * (250 + wave * 50),
                life: 1.0 + wave * 0.2,
                maxLife: 1.0 + wave * 0.2,
                size: Math.random() * 4 + 2,
                color: blueColors[wave % blueColors.length] ?? '#87ceeb',
                alpha: 1,
                type: 'circle',
              });
            }, i * 25 + wave * 200);
          }
        }
      }
    }

    // GOLDEN RING CASCADES - Directional expanding rings
    for (let ring = 0; ring < 4; ring++) {
      setTimeout(() => {
        for (let i = 0; i < 30; i++) {
          let angle: number;

          if (flipDirections.length > 0 && Math.random() < 0.4) {
            // 40% 機率朝向翻轉方向
            const directionIndex = Math.floor((i / 30) * flipDirections.length);
            const direction = flipDirections[directionIndex] || flipDirections[0];
            if (direction) {
              angle = direction.angle + (Math.random() - 0.5) * (Math.PI / 3); // 60度擴散
            } else {
              angle = (i / 30) * Math.PI * 2;
            }
          } else {
            // 60% 機率保持環形擴散
            angle = (i / 30) * Math.PI * 2;
          }

          const speed = 180 + ring * 60;

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1.2,
            maxLife: 1.2,
            size: Math.random() * 3 + 2,
            color: blueColors[ring % blueColors.length] ?? '#87ceeb',
            alpha: 1,
            type: 'circle',
          });
        }
      }, ring * 150);
    }

    // GOLDEN FOUNTAIN
    for (let i = 0; i < 60 + flippedCount * 8; i++) {
      setTimeout(() => {
        this.particles.push({
          x: x + (Math.random() - 0.5) * 30,
          y: y + Math.random() * 15,
          vx: (Math.random() - 0.5) * 300,
          vy: -Math.random() * 350 - 150,
          life: 1.8,
          maxLife: 1.8,
          size: Math.random() * 3 + 1,
          color: blueColors[Math.floor(Math.random() * blueColors.length)] ?? '#4169e1',
          alpha: 1,
          type: 'spark',
        });
      }, Math.random() * 400);
    }

    // SPARKLE BURST
    for (let i = 0; i < 25; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 100 + 50;

      this.particles.push({
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        vx: Math.cos(angle) * 100,
        vy: Math.sin(angle) * 100,
        life: 0.8,
        maxLife: 0.8,
        size: Math.random() * 3 + 1,
        color: '#87ceeb',
        alpha: 1,
        type: 'circle',
      });
    }
  }

  private createBEffect(x: number, y: number, flippedCount: number): void {
    // SOLID B-grade effect - SPRING GREEN POWER!
    const greenColors = ['#90ee90', '#98fb98', '#00ff7f', '#00fa9a', '#7cfc00', '#7fff00', '#adff2f', '#9aff9a', '#b4ffb4'];

    console.log('💚 GOOD B-GRADE MOVE! 💚');

    // DOUBLE RING BURST
    for (let ring = 0; ring < 2; ring++) {
      setTimeout(() => {
        for (let i = 0; i < 25; i++) {
          const angle = (i / 25) * Math.PI * 2;
          const speed = 120 + ring * 40;

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1.0,
            maxLife: 1.0,
            size: Math.random() * 2 + 2,
            color: greenColors[ring % greenColors.length] ?? '#90ee90',
            alpha: 1,
            type: 'circle',
          });
        }
      }, ring * 100);
    }

    // GREEN SPARKS
    for (let i = 0; i < 20 + flippedCount * 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 150 + 80;

      setTimeout(() => {
        this.particles.push({
          x: x,
          y: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1.2,
          maxLife: 1.2,
          size: Math.random() * 2 + 1,
          color: greenColors[Math.floor(Math.random() * greenColors.length)] ?? '#00ff7f',
          alpha: 1,
          type: 'spark',
        });
      }, Math.random() * 200);
    }

    // RADIATING LINES
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;

      for (let j = 0; j < 5; j++) {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * (150 + j * 30),
            vy: Math.sin(angle) * (150 + j * 30),
            life: 0.8,
            maxLife: 0.8,
            size: 2,
            color: '#90ee90',
            alpha: 1,
            type: 'circle',
          });
        }, j * 50);
      }
    }
  }

  private createGreenLotusEffect(x: number, y: number, flippedPositions: Array<{x: number, y: number}>): void {
    console.log('🌸 青蓮劍歌 - Green Lotus Effect! 🌸');

    // Convert flipped positions to target positions for the effect
    const targetPositions: Position[] = flippedPositions.map(pos => ({ x: pos.x, y: pos.y }));

    // Create the Green Lotus effect
    const greenLotus = new GreenLotusEffect(x, y, targetPositions);
    this.greenLotusEffects.push(greenLotus);
  }

  private createUnlimitedBladeWorksEffect(x: number, y: number, flippedPositions: Array<{x: number, y: number}>): void {
    console.log('⚔️ 劍刃迴響 - Blade Echo Effect! ⚔️');

    // Convert flipped positions to target positions for the effect
    const targetPositions: Position[] = flippedPositions.map(pos => ({ x: pos.x, y: pos.y }));

    // Create the UBW effect
    const ubwEffect = new UnlimitedBladeWorksEffect(x, y, targetPositions);
    this.ubwEffects.push(ubwEffect);
  }

  private createDivineThunderEffect(x: number, y: number, flippedPositions: Array<{x: number, y: number}>): void {
    console.log('⚡ 神劍御雷 - Divine Thunder Strike! ⚡');

    // Convert flipped positions to target positions for the effect
    const targetPositions: Position[] = flippedPositions.map(pos => ({ x: pos.x, y: pos.y }));

    // Create the Divine Thunder effect
    const thunderEffect = new DivineSwordThunderEffect(x, y, targetPositions);
    this.divineThunderEffects.push(thunderEffect);
  }

  private createNineFinaleEffect(x: number, y: number, flippedPositions: Array<{x: number, y: number}>): void {
    console.log('🔥 九俱焚滅 - Nine Finale Destruction! 🔥');

    // Convert flipped positions to target positions for the effect
    const targetPositions: Position[] = flippedPositions.map(pos => ({ x: pos.x, y: pos.y }));

    // Create the Nine Finale effect
    const finaleEffect = new NineFinaleEffect(x, y, targetPositions);
    this.nineFinaleEffects.push(finaleEffect);
  }

  private createIceSpearEffect(x: number, y: number, flippedPositions: Array<{x: number, y: number}>): void {
    console.log('❄️ 滅劍羅淵 - Ice Spear Abyss! ❄️');

    // Convert flipped positions to target positions for the effect
    const targetPositions: Position[] = flippedPositions.map(pos => ({ x: pos.x, y: pos.y }));

    // Create the Ice Spear effect
    const iceSpearEffect = new IceSpearEffect(x, y, targetPositions);
    this.iceSpearEffects.push(iceSpearEffect);
  }

  private createCEffect(x: number, y: number): void {
    // BASIC C-grade effect - Light gray elegance
    const grayColors = ['#d3d3d3', '#c0c0c0', '#dcdcdc', '#f5f5f5', '#e0e0e0', '#f8f8f8', '#b8b8b8', '#a9a9a9', '#d8d8d8', '#e6e6e6'];

    console.log('⚪ Basic C-Grade Move');

    // SIMPLE RING - Still noticeable
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;

      this.particles.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * 80,
        vy: Math.sin(angle) * 80,
        life: 0.8,
        maxLife: 0.8,
        size: Math.random() * 1.5 + 1,
        color: grayColors[i % grayColors.length] ?? '#d3d3d3',
        alpha: 0.8,
        type: 'circle',
      });
    }

    // SMALL SPARKLES
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 60 + 40;

      this.particles.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.6,
        maxLife: 0.6,
        size: 1,
        color: '#d3d3d3',
        alpha: 0.7,
        type: 'spark',
      });
    }
  }

  // Create flip animation particles - SPECTACULAR ENHANCED!
  createFlipEffect(x: number, y: number, color: string, grade?: Grade): void {
    // Much more spectacular flip effects with grade-based colors!
    const isBlack = color === '#000000';

    // Get grade-based colors or fallback to basic colors
    let gradeColors: string[];
    let intensity: number;

    if (grade) {
      switch (grade) {
        case 'SSS':
          // Use cosmic_burst colors - golden theme
          gradeColors = ['#ffd700', '#ffef94', '#fff68f', '#ffffe0', '#fffacd'];
          intensity = 3.0;
          break;
        case 'SS':
          // Use supernova colors - orange theme
          gradeColors = ['#ff8c00', '#ffa500', '#ff7f00', '#ff6347', '#ff4500'];
          intensity = 2.5;
          break;
        case 'S':
          // Use light purple theme - 淺紫色系主題
          gradeColors = ['#dda0dd', '#e6b8ea', '#d8bfd8', '#e0b0ff', '#dbb2ff'];
          intensity = 2.0;
          break;
        case 'A':
          // Use light blue theme - 淺藍色系主題
          gradeColors = ['#87ceeb', '#add8e6', '#b0e0e6', '#afeeee', '#e0ffff'];
          intensity = 1.5;
          break;
        case 'B':
          // Use light green theme - 淺綠色系主題
          gradeColors = ['#90ee90', '#98fb98', '#b4ffb4', '#c8ffc8', '#a8e6a8'];
          intensity = 1.2;
          break;
        case 'C':
          // Use white/gray theme - 白灰色系主題
          gradeColors = ['#d3d3d3', '#c0c0c0', '#e0e0e0', '#f5f5f5', '#b8b8b8'];
          intensity = 1.0;
          break;
        default:
          gradeColors = isBlack ? ['#666666', '#888888', '#555555'] : ['#f0f0f0', '#e8e8e8', '#f5f5f5'];
          intensity = 0.8;
      }
    } else {
      gradeColors = isBlack ? ['#666666', '#888888', '#555555'] : ['#f0f0f0', '#e8e8e8', '#f5f5f5'];
      intensity = 0.8;
    }

    const primaryColor = gradeColors[0] ?? '#ffffff';
    const accentColors = gradeColors;

    console.log(`🔄 Flip Effect: ${isBlack ? 'Black' : 'White'} piece flipped! Grade: ${grade || 'Basic'}`);

    // EXPLOSIVE FLIP BURST - Intensity based on grade!
    const burstCount = Math.floor(15 * intensity);
    for (let i = 0; i < burstCount; i++) {
      const angle = (i / burstCount) * Math.PI * 2;
      const speed = (Math.random() * 60 + 60) * intensity;

      this.particles.push({
        x: x,
        y: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.6 * intensity,
        maxLife: 0.6 * intensity,
        size: (Math.random() * 1.5 + 1) * intensity,
        color: i % 3 === 0 ? primaryColor : accentColors[i % accentColors.length] ?? primaryColor,
        alpha: 0.9,
        type: i % 2 === 0 ? 'spark' : 'circle',
      });
    }

    // RING EXPANSION - Visual flip wave (intensity-based)
    const ringCount = Math.floor(2 + intensity);
    for (let ring = 0; ring < ringCount; ring++) {
      setTimeout(() => {
        const particlesPerRing = Math.floor(12 + intensity * 4);
        for (let i = 0; i < particlesPerRing; i++) {
          const angle = (i / particlesPerRing) * Math.PI * 2;
          const speed = (80 + ring * 30) * intensity;

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 0.6 + ring * 0.2,
            maxLife: 0.6 + ring * 0.2,
            size: ring === 0 ? 2 : 1,
            color: primaryColor,
            alpha: 0.7 - ring * 0.2,
            type: 'circle',
          });
        }
      }, ring * 80);
    }

    // FLIP SPARKLE TRAIL - Cascading sparkles
    for (let i = 0; i < 15; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 60 + 40;

      setTimeout(() => {
        this.particles.push({
          x: x + (Math.random() - 0.5) * 20,
          y: y + (Math.random() - 0.5) * 20,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0.7,
          maxLife: 0.7,
          size: Math.random() * 1.5 + 1,
          color: accentColors[Math.floor(Math.random() * accentColors.length)] ?? '#d3d3d3',
          alpha: 0.8,
          type: 'spark',
        });
      }, Math.random() * 200);
    }

    // MAGIC DUST - Floating ambient particles
    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        this.particles.push({
          x: x + (Math.random() - 0.5) * 30,
          y: y + (Math.random() - 0.5) * 30,
          vx: (Math.random() - 0.5) * 30,
          vy: -Math.random() * 40 - 20,
          life: 1.2,
          maxLife: 1.2,
          size: Math.random() * 1 + 0.5,
          color: primaryColor,
          alpha: 0.6,
          type: 'circle',
        });
      }, Math.random() * 300);
    }

    // FLIP SHOCKWAVE - Single expanding ring
    setTimeout(() => {
      for (let angle = 0; angle < Math.PI * 2; angle += 0.2) {
        this.particles.push({
          x: x,
          y: y,
          vx: Math.cos(angle) * 150,
          vy: Math.sin(angle) * 150,
          life: 0.4,
          maxLife: 0.4,
          size: 1.5,
          color: isBlack ? '#333333' : '#f9f9f9',
          alpha: 0.5,
          type: 'circle',
        });
      }
    }, 100);
  }

  // Clean up
  destroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    this.particles = [];
    this.canvas.remove();
  }

  // Get particle count for performance monitoring
  getParticleCount(): number {
    return this.particles.length;
  }

  // Special situation effects
  private createCornerExplosion(x: number, y: number): void {
    // Golden castle walls effect for corner captures
    const castleColors = ['#ffd700', '#ffdf00', '#ffa500'];

    // Castle wall pattern
    for (let wall = 0; wall < 4; wall++) {
      const angle = (wall / 4) * Math.PI * 2;

      for (let brick = 0; brick < 20; brick++) {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * 300 + (Math.random() - 0.5) * 100,
            vy: Math.sin(angle) * 300 + (Math.random() - 0.5) * 100,
            life: 1.5,
            maxLife: 1.5,
            size: Math.random() * 3 + 2,
            color: castleColors[brick % castleColors.length] ?? '#ffd700',
            alpha: 1,
            type: 'circle',
          });
        }, brick * 30 + wall * 200);
      }
    }
  }

  private createComebackEffect(x: number, y: number): void {
    // Phoenix rising effect for comeback moves
    const phoenixColors = ['#ff4500', '#ff6347', '#ffa500', '#ff1493'];

    // Rising phoenix flames
    for (let i = 0; i < 80; i++) {
      setTimeout(() => {
        this.particles.push({
          x: x + (Math.random() - 0.5) * 60,
          y: y + Math.random() * 30,
          vx: (Math.random() - 0.5) * 200,
          vy: -Math.random() * 400 - 200,
          life: 2.0,
          maxLife: 2.0,
          size: Math.random() * 4 + 2,
          color: phoenixColors[Math.floor(Math.random() * phoenixColors.length)] ?? '#ff6347',
          alpha: 0.9,
          type: 'spark',
        });
      }, Math.random() * 600);
    }
  }

  private createMassiveEffect(x: number, y: number): void {
    // Tsunami wave for massive flips
    const waveColors = ['#0080ff', '#00bfff', '#87ceeb', '#b0e0e6'];

    // Wave fronts
    for (let wave = 0; wave < 6; wave++) {
      setTimeout(() => {
        for (let particle = 0; particle < 40; particle++) {
          const angle = (particle / 40) * Math.PI * 2;

          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * (200 + wave * 50),
            vy: Math.sin(angle) * (200 + wave * 50),
            life: 1.0 + wave * 0.2,
            maxLife: 1.0 + wave * 0.2,
            size: Math.random() * 3 + 1,
            color: waveColors[wave % waveColors.length] ?? '#0080ff',
            alpha: 0.8,
            type: 'circle',
          });
        }
      }, wave * 150);
    }
  }

  private createDominationEffect(x: number, y: number): void {
    // Crown and scepter effect for domination
    const royalColors = ['#9932cc', '#8b00ff', '#ffd700', '#daa520'];

    // Crown jewels
    for (let jewel = 0; jewel < 12; jewel++) {
      const angle = (jewel / 12) * Math.PI * 2;

      setTimeout(() => {
        this.particles.push({
          x: x + Math.cos(angle) * 50,
          y: y + Math.sin(angle) * 50,
          vx: Math.cos(angle) * 150,
          vy: Math.sin(angle) * 150,
          life: 1.8,
          maxLife: 1.8,
          size: Math.random() * 4 + 3,
          color: royalColors[jewel % royalColors.length] ?? '#9932cc',
          alpha: 1,
          type: 'circle',
        });
      }, jewel * 80);
    }
  }

  // Chain reaction effect
  createChainEffect(x: number, y: number): void {
    if (!this.enabled) {return;}

    // Lightning chain for chain reactions
    const lightningColors = ['#ffff00', '#ffffff', '#00ffff'];

    // Lightning bolts
    for (let bolt = 0; bolt < 8; bolt++) {
      const angle = (bolt / 8) * Math.PI * 2;

      for (let segment = 0; segment < 10; segment++) {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y,
            vx: Math.cos(angle) * (100 + segment * 20) + (Math.random() - 0.5) * 60,
            vy: Math.sin(angle) * (100 + segment * 20) + (Math.random() - 0.5) * 60,
            life: 0.4,
            maxLife: 0.4,
            size: 2,
            color: lightningColors[segment % lightningColors.length] ?? '#ffff00',
            alpha: 1,
            type: 'spark',
          });
        }, segment * 20 + bolt * 50);
      }
    }
  }

  /**
   * 創建飄起的等級文字效果
   */
  createFloatingGradeText(x: number, y: number, grade: Grade): void {
    if (!this.enabled) {return;}

    // 定義每個等級的顏色和大小
    const gradeConfig = {
      'C': { color: '#d3d3d3', fontSize: 24 },
      'B': { color: '#90ee90', fontSize: 28 },
      'A': { color: '#87ceeb', fontSize: 42 },
      'S': { color: '#9932cc', fontSize: 48 },
      'SS': { color: '#ff8c00', fontSize: 56 },
      'SSS': { color: '#ffd700', fontSize: 64 },
    };

    const config = gradeConfig[grade];
    if (!config) {return;}

    console.log(`💫 Creating floating text: ${grade} at (${x}, ${y})`);

    // 嘗試創建 Canvas 文字粒子
    if (this.ctx && this.canvas) {
      this.particles.push({
        x: x,
        y: y,
        vx: 0, // 不水平移動
        vy: -80, // 向上飄起，速度根據等級調整
        life: 2.0, // 顯示2秒
        maxLife: 2.0,
        size: config.fontSize,
        color: config.color,
        alpha: 1.0,
        type: 'text',
        text: grade,
        fontSize: config.fontSize,
      });

      // 為高等級創建額外的文字效果
      if (grade === 'SSS') {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y - 10,
            vx: 0,
            vy: -60,
            life: 2.5,
            maxLife: 2.5,
            size: 72,
            color: '#ffa500',
            alpha: 0.7,
            type: 'text',
            text: grade,
            fontSize: 72,
          });
        }, 100);
      } else if (grade === 'SS') {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y - 8,
            vx: 0,
            vy: -65,
            life: 2.2,
            maxLife: 2.2,
            size: 64,
            color: '#ff4500',
            alpha: 0.6,
            type: 'text',
            text: grade,
            fontSize: 64,
          });
        }, 80);
      } else if (grade === 'S') {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y - 6,
            vx: 0,
            vy: -70,
            life: 2.0,
            maxLife: 2.0,
            size: 54,
            color: '#ba55d3',
            alpha: 0.5,
            type: 'text',
            text: grade,
            fontSize: 54,
          });
        }, 60);
      } else if (grade === 'A') {
        setTimeout(() => {
          this.particles.push({
            x: x,
            y: y - 5,
            vx: 0,
            vy: -75,
            life: 1.8,
            maxLife: 1.8,
            size: 48,
            color: '#4169e1',
            alpha: 0.4,
            type: 'text',
            text: grade,
            fontSize: 48,
          });
        }, 40);
      }
    } else {
      // 使用 CSS 後備方案
      this.createCSSFloatingText(x, y, grade);
    }
  }

  /**
   * CSS 後備方案創建飄起文字
   */
  private createCSSFloatingText(x: number, y: number, grade: Grade): void {
    console.log(`🎨 Using CSS fallback for floating text: ${grade}`);

    const container = this.canvas.parentElement;
    if (!container) {return;}

    // 創建文字元素
    const textElement = document.createElement('div');
    textElement.className = `floating-grade-text grade-${grade.toLowerCase()}`;
    textElement.textContent = grade;

    // 設定位置
    textElement.style.left = `${x}px`;
    textElement.style.top = `${y}px`;
    textElement.style.transform = 'translate(-50%, -50%)';

    // 新增到容器
    container.appendChild(textElement);

    // 2秒後移除
    setTimeout(() => {
      if (textElement.parentNode) {
        textElement.parentNode.removeChild(textElement);
      }
    }, 2000);
  }
}